import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { hasWatcherAccess, hasDeepDiverAccess, getTierLabel, TIER_THRESHOLDS, SUBSCRIPTION_PRICES, formatDnetAmount } from '../utils/tiers';
import { apiFetch, getApiHost } from '../utils/api';
import { trackEvent } from '../utils/analytics';
interface Props {
  required: 'watcher' | 'deep_diver';
  feature?: string;            // short label for the gate copy ("the holder graph", "trending feed")
  mint?: string;               // when provided, allow per-token credit unlock
  onUnlock?: () => void;       // called after a successful credit unlock so the parent can refetch
  children?: React.ReactNode;  // optional — callers using TierGate as a terminal screen omit this
}

/**
 * Progressive ask. Three states, in order:
 *   1. signed out             → "Log in to continue" (only auth ask)
 *   2. signed in, no tier     → upgrade options + (if `mint` given) burn-credit unlock for this token
 *   3. signed in, tier ok     → render children
 *
 * The `mint` path makes per-feature unlocks possible without forcing the
 * tier ask: a free user can spend a credit to view holder details for
 * one token, then come back later to upgrade if they want it across all
 * tokens. Trending and other non-token-scoped gates omit `mint`.
 */
const TierGate: React.FC<Props> = ({ required, feature, mint, onUnlock, children }) => {
  const { user, isAuthenticated, isLoading, openLogin, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [unlocking, setUnlocking] = useState(false);
  const [unlockError, setUnlockError] = useState<string | null>(null);

  const handleCancel = () => {
    // Prefer the natural parent (token details) over browser-back, so a
    // direct URL load still has somewhere sensible to go.
    if (mint) navigate(`/token/${mint}`);
    else if (window.history.length > 1) navigate(-1);
    else navigate('/');
  };

  const willGate = !isLoading && (!isAuthenticated || !(required === 'watcher' ? hasWatcherAccess(user?.tier) : hasDeepDiverAccess(user?.tier)));
  // Hook called every render; only emits when the gate is actually shown.
  useEffect(() => {
    if (willGate) {
      trackEvent('gate_shown', { feature, tier: required, mint: mint ?? null });
    }
  }, [willGate, feature, required, mint]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin w-8 h-8 border-2 border-crypto-accent border-t-transparent rounded-full" />
      </div>
    );
  }

  const hasTier = required === 'watcher' ? hasWatcherAccess(user?.tier) : hasDeepDiverAccess(user?.tier);
  if (isAuthenticated && hasTier) return <>{children}</>;

  const featureLabel = feature ?? 'this';
  const tierLabel = getTierLabel(required);
  const threshold = TIER_THRESHOLDS[required];
  const subPrice = SUBSCRIPTION_PRICES[required];
  const credits = user?.credits ?? 0;
  const canUnlockWithCredit = !!mint && isAuthenticated && credits >= 1;

  const handleUnlock = async () => {
    if (!mint) return;
    trackEvent('credit_unlock_clicked', { feature, mint });
    setUnlocking(true);
    setUnlockError(null);
    try {
      const res = await apiFetch(`${getApiHost()}/api/token-safety/${mint}/unlock`, { method: 'POST' });
      if (res.status === 402) {
        setUnlockError('You don\'t have enough credits.');
        return;
      }
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setUnlockError(data.error || 'Unlock failed.');
        return;
      }
      await refreshUser();
      trackEvent('credit_unlock_succeeded', { feature, mint });
      onUnlock?.();
    } catch (err) {
      setUnlockError(err instanceof Error ? err.message : 'Unlock failed.');
    } finally {
      setUnlocking(false);
    }
  };

  return (
    <div className="max-w-md mx-auto my-16 px-4 text-center">
      <div className="bg-crypto-gray border border-gray-700 rounded-xl p-8">
        {!isAuthenticated ? (
          <>
            <div className="text-5xl mb-4">🔐</div>
            <h2 className="text-xl font-semibold text-white mb-2">Log in to continue</h2>
            <p className="text-gray-400 text-sm mb-2">
              Viewing {featureLabel} requires an account.
            </p>
            <p className="text-crypto-accent text-xs mb-6">
              ✨ New accounts get 10 free credits — unlock this and other tokens with credits.
            </p>
            <button
              onClick={() => {
                openLogin('gate', undefined, { feature, tier: required });
              }}
              className="bg-crypto-accent hover:bg-crypto-accent/80 text-crypto-dark font-medium px-6 py-2.5 rounded-lg transition-colors"
            >
              Log In / Sign Up
            </button>
          </>
        ) : (
          <>
            <div className="text-5xl mb-4">{required === 'deep_diver' ? '🔱' : '👁️'}</div>
            <h2 className="text-xl font-semibold text-white mb-2">{tierLabel} tier required</h2>
            <p className="text-gray-400 text-sm mb-5">
              {featureLabel.charAt(0).toUpperCase() + featureLabel.slice(1)} is available to {tierLabel} tier and above.
            </p>

            {/* Subscribe-first ordering (swapped from credit-first on
                2026-05-27). Hypothesis: leading with the higher-value
                action lifts subscription conversion off this surface.
                Compare gate_shown → upgrade_from_gate_clicked rate
                against the prior 2 weeks to evaluate. If neutral or
                worse, swap back. */}
            <p className="text-gray-400 text-sm mb-1">Upgrade to {tierLabel} for unlimited access:</p>
            <p className="text-gray-500 text-xs mb-4">
              Hold {formatDnetAmount(threshold)} $DNET or subscribe (${subPrice}/mo).
            </p>
            <div className="flex justify-center mb-5">
              <Link
                to="/tiers"
                onClick={() => trackEvent('upgrade_from_gate_clicked', { source: 'gate', feature, tier: required, mint: mint ?? null })}
                className="bg-crypto-accent hover:bg-crypto-accent/80 text-crypto-dark font-medium px-6 py-2.5 rounded-lg transition-colors"
              >
                Compare plans
              </Link>
            </div>

            {mint && (
              <div className="border border-gray-700 rounded-lg p-4 bg-crypto-dark/40">
                <p className="text-white text-sm font-medium mb-1">Or just this token</p>
                <p className="text-gray-500 text-xs mb-3">
                  Burn 1 credit and {featureLabel} stays unlocked for this token.{' '}
                  <span className="text-gray-400">You have {credits.toLocaleString()} credit{credits === 1 ? '' : 's'}.</span>
                </p>
                <button
                  onClick={handleUnlock}
                  disabled={unlocking || !canUnlockWithCredit}
                  className="w-full bg-gray-700 hover:bg-gray-600 disabled:bg-gray-800 disabled:text-gray-600 disabled:cursor-not-allowed text-white font-medium px-4 py-2 rounded-lg transition-colors text-sm"
                >
                  {unlocking ? 'Unlocking…' : canUnlockWithCredit ? '🔓 Burn 1 credit' : 'Not enough credits'}
                </button>
                {unlockError && <p className="text-red-400 text-xs mt-2">{unlockError}</p>}
                {!canUnlockWithCredit && credits === 0 && (
                  <p className="text-gray-500 text-xs mt-2">
                    <Link to="/profile/credits" className="text-crypto-accent hover:underline">Get credits</Link>
                  </p>
                )}
              </div>
            )}
          </>
        )}
        <div className="mt-6">
          <button
            onClick={handleCancel}
            className="text-gray-500 hover:text-gray-300 text-sm transition-colors"
          >
            ← Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default TierGate;
